import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import type { NotificationDto } from '@shared/notification.js';
import { useSignaling } from '../../lib/signaling/useSignaling';
import {
  useNotificationStore,
  selectBadgeCount,
  selectHasStickyDot,
} from '../../store/useNotificationStore';

const TITLES: Record<string, string> = {
  friend_request_received: 'New friend request',
  friend_request_accepted: 'Friend request accepted',
  friend_removed: 'A friend removed you',
  ban_received: 'Your account was restricted',
  ban_appeal_decision: 'Decision on your appeal',
  match_warning_received: 'Moderation warning',
  verification_completed: 'Verification completed',
  friendship_anniversary: 'Friendship anniversary',
  birthday_self: 'Happy birthday!',
  birthday_friend: "A friend's birthday",
  penpal_turn_expiring: 'Your turn is expiring soon',
};

function titleFor(n: NotificationDto): string {
  return TITLES[n.type_key] ?? 'Notificare';
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'acum';
  if (min < 60) return `acum ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `acum ${h} h`;
  const d = Math.floor(h / 24);
  return `acum ${d} zile`;
}

/**
 * BellIcon notification center (CF-2400). Mounted globally for authenticated
 * users; subscribes to WS `notification_new` for live updates.
 */
export function NotificationCenter(): JSX.Element {
  const signaling = useSignaling();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const notifications = useNotificationStore((s) => s.notifications);
  const badgeCount = useNotificationStore(selectBadgeCount);
  const hasStickyDot = useNotificationStore(selectHasStickyDot);
  const hydrate = useNotificationStore((s) => s.hydrate);
  const addRealtime = useNotificationStore((s) => s.addRealtime);
  const markSeen = useNotificationStore((s) => s.markSeen);
  const markRead = useNotificationStore((s) => s.markRead);
  const markAllRead = useNotificationStore((s) => s.markAllRead);

  const seenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  useEffect(() => {
    const off = signaling.on('notification_new', (msg) => addRealtime(msg.notification));
    return () => off();
  }, [signaling, addRealtime]);

  const open = Boolean(anchorEl);

  function handleOpen(e: React.MouseEvent<HTMLButtonElement>): void {
    setAnchorEl(e.currentTarget);
    // "Seen this session" — anything unread still visible after 2s no longer
    // contributes to the number badge (but stays unread in the DB).
    seenTimer.current = setTimeout(() => {
      for (const n of useNotificationStore.getState().notifications) {
        if (n.read_at === null) markSeen(n.id);
      }
    }, 2000);
  }

  function handleClose(): void {
    if (seenTimer.current) clearTimeout(seenTimer.current);
    setAnchorEl(null);
  }

  function handleItemClick(n: NotificationDto): void {
    void markRead(n.id);
    const url = typeof n.payload?.['url'] === 'string' ? (n.payload['url'] as string) : null;
    handleClose();
    if (url) navigate(url);
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleOpen} aria-label="Notifications">
          <Badge
            color="error"
            overlap="circular"
            badgeContent={badgeCount}
            variant={badgeCount > 0 ? 'standard' : hasStickyDot ? 'dot' : 'standard'}
            invisible={badgeCount === 0 && !hasStickyDot}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxWidth: '95vw' } } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Notifications
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Mark all as read">
              <span>
                <IconButton size="small" onClick={() => void markAllRead()} disabled={badgeCount === 0}>
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Notification settings">
              <IconButton
                size="small"
                onClick={() => {
                  handleClose();
                  navigate('/settings/notifications');
                }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              You have no notifications.
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 420, overflowY: 'auto', py: 0 }}>
            {notifications.map((n) => {
              const unresolvedSticky = n.is_sticky_important && n.resolved_at === null;
              return (
                <ListItemButton
                  key={n.id}
                  onClick={() => handleItemClick(n)}
                  sx={{
                    bgcolor: n.read_at === null ? 'action.hover' : 'transparent',
                    borderLeft: unresolvedSticky ? '3px solid' : '3px solid transparent',
                    borderLeftColor: unresolvedSticky ? 'error.main' : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={n.read_at === null ? 600 : 400}>
                        {titleFor(n)}
                      </Typography>
                    }
                    secondary={relativeTime(n.created_at)}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="small" onClick={() => { handleClose(); navigate('/settings/notifications'); }}>
            Manage notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
}
